import React, { useState } from 'react'
import { Button, Card, Flex, Input, Popconfirm, Switch, Typography } from 'antd'
import { DeleteOutlined, EditOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

export type EditableMessage = {
    id: string
    text: string
    autoReject?: boolean
}

export type EditableMessageListProps = {
    messages: EditableMessage[]
    onChange: (messages: EditableMessage[]) => void
    title?: string
    addButtonText?: string
    placeholder?: string
    className?: string
    showAutoReject?: boolean
    max?: number
    maxLength?: number
}

type SortableMessageItemProps = {
    message: EditableMessage
    onEdit: (id: string, content: string) => void
    onDelete: (id: string) => void
    onToggleAutoReject?: (id: string, autoReject: boolean) => void
    showAutoReject?: boolean
    maxLength?: number
}

function SortableMessageItem({
    message,
    onEdit,
    onDelete,
    onToggleAutoReject,
    showAutoReject,
    maxLength,
}: SortableMessageItemProps) {
    const { t } = useTranslation()
    const [isEditing, setIsEditing] = useState(false)
    const [editValue, setEditValue] = useState(message.text)

    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id: message.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        opacity: isDragging ? 0.5 : 1,
    }

    const handleSave = () => {
        onEdit(message.id, editValue)
        setIsEditing(false)
    }

    const handleCancel = () => {
        setEditValue(message.text)
        setIsEditing(false)
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="grid grid-cols-[24px_minmax(0,1fr)_56px] items-start gap-2 border-b border-gray-200 py-4 @[600px]:grid-cols-[24px_minmax(0,1fr)_144px_56px]"
        >
            <Button
                type="text"
                icon={<HolderOutlined />}
                size="small"
                className="cursor-grab text-gray-400 hover:text-gray-600 flex-shrink-0 mt-1"
                {...attributes}
                {...listeners}
                aria-label={t('Reorder')}
            />

            <div className={clsx('min-w-0', isEditing && 'col-span-2 @[600px]:col-span-3')}>
                {isEditing ? (
                    <Flex
                        vertical
                        gap="small"
                    >
                        <Input.TextArea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            autoSize={{ minRows: 2, maxRows: 6 }}
                            maxLength={maxLength}
                            onPressEnter={(e) => {
                                if (e.shiftKey) return
                                e.preventDefault()
                                handleSave()
                            }}
                        />
                        <Flex
                            gap="small"
                            justify="end"
                        >
                            <Button
                                size="small"
                                onClick={handleCancel}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                size="small"
                                type="primary"
                                onClick={handleSave}
                                disabled={!editValue.trim()}
                            >
                                {t('Save')}
                            </Button>
                        </Flex>
                    </Flex>
                ) : (
                    <Typography.Text className="block whitespace-pre-wrap">{message.text}</Typography.Text>
                )}
            </div>

            {showAutoReject && !isEditing && (
                <Flex
                    align="center"
                    gap="small"
                    className="col-start-2 col-span-2 row-start-2 @[600px]:col-start-3 @[600px]:col-span-1 @[600px]:row-start-1 @[600px]:justify-center self-center"
                >
                    <Switch
                        size="small"
                        checked={message.autoReject || false}
                        aria-label={t('Reject on a negative answer to: {{question}}', { question: message.text })}
                        onChange={(checked) => onToggleAutoReject?.(message.id, checked)}
                    />
                    <Typography.Text
                        type="secondary"
                        className="text-xs @[600px]:hidden"
                    >
                        {t('Reject on a negative answer')}
                    </Typography.Text>
                </Flex>
            )}

            {!isEditing && (
                <Flex
                    gap="small"
                    className="col-start-3 row-start-1 @[600px]:col-start-4"
                >
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        aria-label={t('Edit')}
                        size="small"
                        onClick={() => {
                            setEditValue(message.text)
                            setIsEditing(true)
                        }}
                        className="text-gray-400 hover:text-blue-500"
                    />
                    <Popconfirm
                        title={t('Delete message')}
                        description={t('Are you sure you want to delete this message?')}
                        onConfirm={() => onDelete(message.id)}
                        okText={t('Yes')}
                        cancelText={t('No')}
                    >
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            aria-label={t('Delete message')}
                            size="small"
                            className="text-gray-400 hover:text-red-500"
                        />
                    </Popconfirm>
                </Flex>
            )}
        </div>
    )
}

export function EditableMessageList({
    messages,
    onChange,
    title,
    addButtonText,
    placeholder,
    className,
    showAutoReject = false,
    max,
    maxLength,
}: EditableMessageListProps) {
    const { t } = useTranslation()
    const [newMessage, setNewMessage] = useState('')
    const [isAdding, setIsAdding] = useState(false)

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event

        if (over && active.id !== over.id) {
            const oldIndex = messages.findIndex((message) => message.id === active.id)
            const newIndex = messages.findIndex((message) => message.id === over.id)

            onChange(arrayMove(messages, oldIndex, newIndex))
        }
    }

    const handleEdit = (id: string, content: string) => {
        onChange(messages.map((message) => (message.id === id ? { ...message, text: content } : message)))
    }

    const handleDelete = (id: string) => {
        onChange(messages.filter((message) => message.id !== id))
    }

    const handleToggleAutoReject = (id: string, autoReject: boolean) => {
        onChange(messages.map((message) => (message.id === id ? { ...message, autoReject } : message)))
    }

    const handleAdd = () => {
        if (!newMessage.trim()) return

        const newMsg: EditableMessage = {
            id: Date.now().toString(),
            text: newMessage.trim(),
            autoReject: false,
        }

        onChange([...messages, newMsg])
        setNewMessage('')
        setIsAdding(false)
    }

    const handleCancel = () => {
        setNewMessage('')
        setIsAdding(false)
    }

    const isFull = max !== undefined && messages.length >= max

    return (
        <div className={clsx('@container', className)}>
            {title && (
                <Typography.Title
                    level={5}
                    className="mb-3"
                >
                    {title}
                </Typography.Title>
            )}

            {showAutoReject && (
                <div className="hidden @[600px]:grid grid-cols-[24px_minmax(0,1fr)_144px_56px] gap-2 pb-2 border-b border-gray-200">
                    <Typography.Text
                        type="secondary"
                        className="col-start-2 text-xs"
                    >
                        {t('Question')}
                    </Typography.Text>
                    <Typography.Text
                        type="secondary"
                        className="text-xs text-center"
                    >
                        {t('Reject on a negative answer')}
                    </Typography.Text>
                </div>
            )}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                <SortableContext
                    items={messages.map((msg) => msg.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <Flex
                        vertical
                        className="mb-4"
                    >
                        {messages.map((message) => (
                            <SortableMessageItem
                                key={message.id}
                                message={message}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onToggleAutoReject={handleToggleAutoReject}
                                showAutoReject={showAutoReject}
                                maxLength={maxLength}
                            />
                        ))}
                    </Flex>
                </SortableContext>
            </DndContext>

            {isAdding ? (
                <Card
                    size="small"
                    className="bg-gray-50 border border-dashed border-gray-300"
                >
                    <Flex
                        vertical
                        gap="small"
                    >
                        <Input.TextArea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder={placeholder || t('Enter message')}
                            autoSize={{ minRows: 2, maxRows: 6 }}
                            maxLength={maxLength}
                            autoFocus
                            onPressEnter={(e) => {
                                if (e.shiftKey) return
                                e.preventDefault()
                                handleAdd()
                            }}
                        />
                        <Flex
                            gap="small"
                            justify="end"
                        >
                            <Button
                                size="small"
                                onClick={handleCancel}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                size="small"
                                type="primary"
                                onClick={handleAdd}
                                disabled={!newMessage.trim()}
                            >
                                {t('Add')}
                            </Button>
                        </Flex>
                    </Flex>
                </Card>
            ) : (
                !isFull && (
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => setIsAdding(true)}
                        className="w-full"
                    >
                        {addButtonText || t('Add message')}
                    </Button>
                )
            )}
        </div>
    )
}
